import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Area `workspace-home` of the console gateway — owned by plan-002 unit **RB-22** (design §15.6.2).
 *
 * First-run progress (`getWorkspaceOnboarding`) and the dashboard's figures
 * (`getWorkspaceUsageSummary`). `DashboardFiguresView.answeredFirstTimePercent` is
 * `number | null` since RB-03 (C21): a day with no inbound conversation has no percentage.
 *
 * ── HOW THIS FILE WORKS (IC-2, created by RB-03) ─────────────────────────────────────────
 * The method signatures below are merged onto `ConsoleGateway` by declaration merging, so
 * RB-22 owns them here and never edits `gateway.ts`. `workspaceHomeGap` is this area's rows of
 * `GATEWAY_GAP`, which `gateway.ts` concatenates; it is EMPTY now that both routes exist.
 * `workspaceHomeApiSlice` is what `apiGateway` spreads. The sandbox (`../sandbox-gateway.ts`)
 * is untouched.
 *
 * ── THE MAPPING (design §15.6.2) ─────────────────────────────────────────────────────────
 * The API sends facts; every word on the two screens is made here (design §15.5.2).
 *
 *   getFirstRunProgress
 *     accountName   ← account_name
 *     createdAtText ← "created " + formatWhen(account_created_at)
 *     task          ← task[], in the API's fixed order; `done` from the API, and the title,
 *                     dek and action label from {@link FIRST_RUN_COPY} (console copy)
 *
 *   getDashboardFigures
 *     greetingName             ← the signed-in person's first name (`GET /v1/session`)
 *     todayText                ← usage_day, worded ("Tuesday 22 September") — the date the
 *                                clock reads in the workspace's zone, which is the day every
 *                                figure below is cut on, so the kicker cannot name a different
 *                                day from the numbers under it
 *     liveConversationCount    ← live_conversation_count
 *     talkSecondToday          ← billed_second_today (whole seconds; the screen shows minutes
 *                                and seconds through `talkTimeText`, so no rounding here —
 *                                owner decision 2026-10-09)
 *     spendToday               ← spend_today (integer minor units, never re-derived here)
 *     answeredFirstTimePercent ← ⌊ answered_first_time_basis_point ÷ 100 ⌋, or `null` when the
 *                                API says `null` (no inbound conversation today, C21)
 *     tier                     ← tier
 *     channelUnitInUse         ← channel_unit_in_use
 *
 * The percent is FLOORED, like the basis point itself: the dashboard only ever says 100% when
 * every caller was answered first time. Talk time is never rounded at all.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  DashboardFiguresView,
  FirstRunProgressView,
  FirstRunTaskView,
  TierView,
} from "../gateway.js";
import { readMoney } from "../conversation-view.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    getFirstRunProgress(): Promise<FirstRunProgressView>;
    getDashboardFigures(): Promise<DashboardFiguresView>;
    /**
     * The ORGANIZATION's concurrency entitlement — conversations at once across every workspace and agent, exactly as
     * admission enforces it (`GET /v1/workspace/usage-summary`'s `concurrency`; INT 2026-10-07). Every member reads it.
     */
    getOrganizationConcurrency(): Promise<number>;
  }

  /**
   * The rest of the prototype's dashboard (FR-APP-3; fidelity wave 2026-09-23), merged onto the
   * view here so the area that owns the dashboard owns its shape. Every field is `null` when the
   * gateway has no source for it — the screen then draws its honest empty state in the same
   * place, never a made-up figure. What the api gateway fills today is noted per field.
   */
  interface DashboardFiguresView {
    /** The setup card. api: derived from `getWorkspaceOnboarding`'s four facts. */
    readonly setup: DashboardSetupView | null;
    /** The plan card's included-minutes line and bar. api: `null` (no included-minutes fact). */
    readonly planUsage: DashboardPlanUsageView | null;
    /** The Minutes-today sparkline and day-over-day line. api: `null` (no time series). */
    readonly minutesTrend: DashboardTrendView | null;
    /** The Spent-today sparkline. api: `null` (no time series). */
    readonly spendTrend: DashboardTrendView | null;
    /** "2 transfers to staff" under Answered first time. api: `null`. */
    readonly transferCountToday: number | null;
    /** Recent calls across agents. api: `null` (no workspace-wide conversation list). */
    readonly recentConversation: readonly DashboardRecentConversationView[] | null;
    /** "Needs your attention". api: `null`. */
    readonly attention: readonly DashboardAttentionView[] | null;
  }
}

/** One numbered step of "Finish setting up your workspace". */
export interface DashboardSetupStepView {
  readonly label: string;
  readonly done: boolean;
}

export interface DashboardSetupView {
  readonly step: readonly DashboardSetupStepView[];
  /** The state chips at the card's right: "Workspace active", "SIP registered", … */
  readonly chip: readonly { readonly label: string; readonly positive: boolean }[];
}

export interface DashboardPlanUsageView {
  /** "25k" — as the plan names it. */
  readonly includedMinuteText: string;
  readonly usedMinute: number;
  readonly includedMinute: number;
  /** "1 Oct 2026". */
  readonly nextCycleText: string;
}

export interface DashboardTrendView {
  /** Oldest first; drawn as a sparkline scaled to its own maximum. */
  readonly point: readonly number[];
  /** "+18% on yesterday", or `null` when there is no yesterday to compare with. */
  readonly deltaText: string | null;
}

export interface DashboardRecentConversationView {
  readonly conversationId: string;
  readonly voiceAgentId: string;
  /** "14:02". */
  readonly timeText: string;
  /** `null` when the caller withheld their number. */
  readonly partyAddress: string | null;
  /** `null` while no outcome was recorded. */
  readonly outcome: string | null;
  readonly billedSeconds: number;
}

export interface DashboardAttentionView {
  readonly tone: "critical" | "muted" | "positive";
  readonly text: string;
  /** A link at the end of the line ("Update the menu"), when there is something to do. */
  readonly action: { readonly label: string; readonly voiceAgentId: string } | null;
}

export const workspaceHomeGap: readonly GatewayGap[] = [];

type TaskCode = FirstRunTaskView["code"];

/** The four codes, in the order the API documents them. Anything else is a malformed response. */
const TASK_CODE: readonly TaskCode[] = [
  "verify_email",
  "create_voice_agent",
  "connect_number",
  "add_card",
];

/**
 * First-run copy — a product constant, so it lives in the console, not in the API (§15.5.2).
 * The same words the in-memory gateway shows, so the two gateways read alike.
 */
const FIRST_RUN_COPY: Readonly<
  Record<TaskCode, (emailAddress: string) => Omit<FirstRunTaskView, "code" | "done">>
> = validatedDisplayView({
  get verify_email() { return (emailAddress: string) => (validatedDisplayView({
    get title() { return portalText("shell", "confirm_your_email_10cd6cd2"); },
    get dek() { return portalText("shell", "we_sent_a_link_to_value_it_only_has_to_be_clicked_once_d118a02f", { v0: String(emailAddress) }); },
    get actionLabel() { return portalText("shell", "resend_the_link_bdf369db"); },
  })); },
  get create_voice_agent() { return () => (validatedDisplayView({
    get title() { return portalText("shell", "create_your_first_agent_e9f219b1"); },
    get dek() { return portalText("shell", "pick_delivery_and_ordering_or_survey_hand_over_your_menu_o_9e4d79e4"); },
    get actionLabel() { return portalText("shell", "pick_an_agent_f7bf2948"); },
  })); },
  get connect_number() { return () => (validatedDisplayView({
    get title() { return portalText("shell", "connect_a_number_f563d967"); },
    get dek() { return portalText("shell", "point_your_telnyx_or_sip_trunk_at_us_take_a_new_number_or_4e3444c4"); },
    get actionLabel() { return portalText("shell", "connect_b65463cb"); },
  })); },
  get add_card() { return () => (validatedDisplayView({
    get title() { return portalText("shell", "add_a_card_5e8cce68"); },
    get dek() { return portalText("shell", "only_needed_when_the_free_minutes_run_out_auto_reload_is_o_533470a1"); },
    get actionLabel() { return portalText("shell", "add_a_card_5e8cce68"); },
  })); },
});

const BASIS_POINT_PER_PERCENT = 100;

/** A whole, non-negative number — a count, a second total or a basis point. */
function readCount(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new MalformedResponseError(path, "a whole number of at least 0", value);
  }
  return value;
}

function readTaskCode(value: unknown, path: string): TaskCode {
  const raw = readText(value, path);
  const found = TASK_CODE.find((one) => one === raw);
  if (found === undefined) {
    throw new MalformedResponseError(path, TASK_CODE.join(" | "), raw);
  }
  return found;
}

function readTier(value: unknown, path: string): TierView {
  const one = readRecord(value, path);
  return {
    tierId: readText(one["tier_id"], `${path}.tier_id`),
    code: readText(one["code"], `${path}.code`),
    name: readText(one["name"], `${path}.name`),
    channelUnitCeiling: readCount(one["channel_unit_ceiling"], `${path}.channel_unit_ceiling`),
    ordinal: readCount(one["ordinal"], `${path}.ordinal`),
  };
}

/** The usage summary's `concurrency.entitlement` — the organization's limit, as admission enforces it. */
function concurrencyEntitlementOfWire(data: Readonly<Record<string, unknown>>): number {
  const concurrency = readRecord(data["concurrency"], "usage_summary.concurrency");
  return readCount(concurrency["entitlement"], "usage_summary.concurrency.entitlement");
}

/** `2026-09-22` → "Tuesday 22 September". The date is a calendar date, so it is worded in UTC. */
function todayTextOf(usageDay: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(usageDay);
  if (parts === null) {
    throw new MalformedResponseError("usage_summary.usage_day", "a YYYY-MM-DD date", usageDay);
  }
  const at = new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])));
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(at);
}

/** "Today 09:02" → "created today 09:02"; "12 Sep 17:28" → "created 12 Sep 17:28". */
function createdAtTextOf(when: string): string {
  return portalText("shell", "created_value_f21ddbf9", { v0: String(when.replace(/^Today\b/, "today").replace(/^Yesterday\b/, "yesterday")) });
}

/** "Giulia Rossi" → "Giulia". A one-word name is its own first name. */
function firstNameOf(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0];
  return first === undefined || first === "" ? displayName : first;
}

/** The signed-in person's name and address, from the auth_session body. Read-only. */
async function currentPersonOf(
  client: WeTalkApiClient,
): Promise<{ readonly displayName: string; readonly emailAddress: string }> {
  const body = readRecord(assertOk(await client.getCurrentAuthSession()), "getCurrentAuthSession");
  const person = readRecord(
    readRecord(body["data"], "getCurrentAuthSession.data")["person"],
    "getCurrentAuthSession.data.person",
  );
  return {
    displayName: readText(person["display_name"], "authSession.person.display_name"),
    emailAddress: readText(person["email"], "authSession.person.email"),
  };
}

/**
 * The dashboard's setup card from the onboarding facts: the workspace exists (it is answering this
 * request), then the four things first run asks for, in the API's order, worded for this card.
 */
function setupOf(answered: Awaited<ReturnType<WeTalkApiClient["getWorkspaceOnboarding"]>>): DashboardSetupView {
  const data = readRecord(
    readRecord(assertOk(answered), "getWorkspaceOnboarding")["data"],
    "getWorkspaceOnboarding.data",
  );
  const done = new Map<TaskCode, boolean>();
  readArray(data["task"], "onboarding.task").forEach((wire, index) => {
    const path = `onboarding.task[${index}]`;
    const one = readRecord(wire, path);
    done.set(readTaskCode(one["code"], `${path}.code`), readBoolean(one["done"], `${path}.done`));
  });
  const doneOf = (code: TaskCode): boolean => done.get(code) === true;
  return {
    step: [
      validatedDisplayView({ get label() { return portalText("shell", "workspace_created_b0f1bbb2"); }, done: true }),
      validatedDisplayView({ get label() { return portalText("shell", "agent_created_55a980a1"); }, done: doneOf("create_voice_agent") }),
      validatedDisplayView({ get label() { return portalText("shell", "number_connected_239cb6a3"); }, done: doneOf("connect_number") }),
      validatedDisplayView({ get label() { return portalText("shell", "email_confirmed_86e753d5"); }, done: doneOf("verify_email") }),
      validatedDisplayView({ get label() { return portalText("shell", "card_added_ad35e84f"); }, done: doneOf("add_card") }),
    ],
    chip: [validatedDisplayView({ get label() { return portalText("shell", "workspace_active_fad730ec"); }, positive: true })],
  };
}

export function workspaceHomeApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<ConsoleGateway, "getFirstRunProgress" | "getDashboardFigures" | "getOrganizationConcurrency"> {
  return {
    async getFirstRunProgress(): Promise<FirstRunProgressView> {
      const [answered, person] = await Promise.all([
        client.getWorkspaceOnboarding(),
        currentPersonOf(client),
      ]);
      const data = readRecord(
        readRecord(assertOk(answered), "getWorkspaceOnboarding")["data"],
        "getWorkspaceOnboarding.data",
      );
      const task = readArray(data["task"], "onboarding.task").map((wire, index) => {
        const path = `onboarding.task[${index}]`;
        const one = readRecord(wire, path);
        const code = readTaskCode(one["code"], `${path}.code`);
        return {
          code,
          done: readBoolean(one["done"], `${path}.done`),
          ...FIRST_RUN_COPY[code](person.emailAddress),
        };
      });
      if (task.map((one) => one.code).join() !== TASK_CODE.join()) {
        throw new MalformedResponseError(
          "onboarding.task",
          `the four codes in order (${TASK_CODE.join(", ")})`,
          task.map((one) => one.code).join(", "),
        );
      }
      return validatedDisplayView({
        accountName: readText(data["account_name"], "onboarding.account_name"),
        get createdAtText() { return createdAtTextOf(
          context.formatWhen(
            readText(data["account_created_at"], "onboarding.account_created_at"),
            context.now(),
          ),
        ); },
        task,
      });
    },

    async getOrganizationConcurrency(): Promise<number> {
      const data = readRecord(
        readRecord(assertOk(await client.getWorkspaceUsageSummary()), "getWorkspaceUsageSummary")["data"],
        "getWorkspaceUsageSummary.data",
      );
      return concurrencyEntitlementOfWire(data);
    },

    async getDashboardFigures(): Promise<DashboardFiguresView> {
      const [answered, person, onboarding] = await Promise.all([
        client.getWorkspaceUsageSummary(),
        currentPersonOf(client),
        client.getWorkspaceOnboarding(),
      ]);
      const data = readRecord(
        readRecord(assertOk(answered), "getWorkspaceUsageSummary")["data"],
        "getWorkspaceUsageSummary.data",
      );
      const basisPoint = data["answered_first_time_basis_point"];
      readText(data["time_zone"], "usage_summary.time_zone");
      return validatedDisplayView({
        greetingName: firstNameOf(person.displayName),
        get todayText() { return todayTextOf(readText(data["usage_day"], "usage_summary.usage_day")); },
        liveConversationCount: readCount(
          data["live_conversation_count"],
          "usage_summary.live_conversation_count",
        ),
        talkSecondToday: readCount(data["billed_second_today"], "usage_summary.billed_second_today"),
        spendToday: readMoney(data["spend_today"], "usage_summary.spend_today"),
        answeredFirstTimePercent:
          basisPoint === null
            ? null
            : Math.floor(
                readCount(basisPoint, "usage_summary.answered_first_time_basis_point") /
                  BASIS_POINT_PER_PERCENT,
              ),
        tier: readTier(data["tier"], "usage_summary.tier"),
        channelUnitInUse: readCount(data["channel_unit_in_use"], "usage_summary.channel_unit_in_use"),
        organizationConcurrency: concurrencyEntitlementOfWire(data),
        setup: setupOf(onboarding),
        planUsage: null,
        minutesTrend: null,
        spendTrend: null,
        transferCountToday: null,
        recentConversation: null,
        attention: null,
      });
    },
  };
}
